import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import {
  createTextMeasurementReference,
  removeMeasurementReference,
} from '../../common/utils/measure-text';
import { ASSIGNMENT_TEXT_CLASSES } from '../constants/assignment-text';

interface TextMeasurementContextValue {
  /** Reference element for measuring assignment text */
  assignmentTextRef: HTMLElement | null;
}

const TextMeasurementContext = createContext<TextMeasurementContextValue | null>(null);

interface TextMeasurementProviderProps {
  children: ReactNode;
}

/**
 * Provides shared reference elements for text measurement across the planner.
 *
 * Creates hidden DOM elements with specific CSS classes that match rendered text,
 * allowing canvas-based text measurement to stay in sync with actual styles.
 */
export const TextMeasurementProvider = ({ children }: TextMeasurementProviderProps) => {
  const [assignmentTextRef, setAssignmentTextRef] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const ref = createTextMeasurementReference(ASSIGNMENT_TEXT_CLASSES);
    setAssignmentTextRef(ref);

    return () => {
      removeMeasurementReference(ref);
    };
  }, []);

  return (
    <TextMeasurementContext.Provider value={{ assignmentTextRef }}>
      {children}
    </TextMeasurementContext.Provider>
  );
};

/**
 * Hook to access text measurement reference elements.
 *
 * @throws Error if used outside TextMeasurementProvider
 */
export const useTextMeasurement = (): TextMeasurementContextValue => {
  const context = useContext(TextMeasurementContext);
  if (!context) {
    throw new Error('useTextMeasurement must be used within TextMeasurementProvider');
  }
  return context;
};
